import { useMemo } from 'react';

import { lodash } from 'helpers';
import { TFunc, useTranslation } from 'libs/i18n';
import { TableColumn } from 'libs/ui';

import { renderBankCountry } from './renders/bankCountry';
import { renderMore } from './renders/more';
import { renderText } from './renders/text';
import { WithdrawalAccountColumnArgs, WithdrawalAccountItem } from './types';

export const getColumns = (
  t: TFunc,
  { shownOptions, editable, onClickOption }: WithdrawalAccountColumnArgs
): TableColumn<WithdrawalAccountItem>[] => {
  const isShowMore = shownOptions && Object.values(shownOptions).some((v) => v);

  return lodash.compact([
    {
      field: 'number',
      title: '',
      width: 64,
      align: 'center',
      renderCell: (item) => renderText(item.number.toString()),
    },
    {
      field: 'bankCountry',
      title: t('client.client.withdrawalAccounts.bankCountry.label'),
      flex: 1,
      minWidth: 216,
      renderCell: (item) => renderBankCountry(item.bankCountry),
    },
    {
      field: 'currency',
      title: t('client.client.withdrawalAccounts.currency.label'),
      flex: 1,
      minWidth: 105,
      renderCell: (item) => renderText(item.currency?.label ?? ''),
    },
    {
      field: 'swiftCode',
      title: t('client.client.withdrawalAccounts.swiftCodeLabel.label'),
      flex: 1,
      minWidth: 184,
      renderCell: (item) => renderText(item.swiftCode),
    },
    {
      field: 'bankName',
      title: t('client.client.withdrawalAccounts.bankName.label'),
      flex: 1,
      minWidth: 184,
      renderCell: (item) => renderText(item.bankName),
    },
    {
      field: 'bankAddress',
      title: t('client.client.withdrawalAccounts.bankAddress.label'),
      flex: 1,
      minWidth: 160,
      renderCell: (item) => renderText(item.bankAddress),
    },
    {
      field: 'checkingAccount',
      title: t('client.client.withdrawalAccounts.checkingAccount.label'),
      flex: 1,
      minWidth: 119,
      renderCell: (item) => renderText(item.checkingAccount),
    },
    {
      field: 'correspondentBankSwiftCode',
      title: t(
        'client.client.withdrawalAccounts.correspondentBankSwiftCode.label'
      ),
      flex: 1,
      minWidth: 240,
      renderCell: (item) => renderText(item.correspondentBankSwiftCode ?? ''),
    },
    {
      field: 'correspondentBankName',
      title: t('client.client.withdrawalAccounts.correspondentBankName.label'),
      flex: 1,
      minWidth: 252,
      renderCell: (item) => renderText(item.correspondentBankName ?? ''),
    },
    {
      field: 'correspondentBankAccountNumber',
      title: t(
        'client.client.withdrawalAccounts.correspondentBankAccountNumber.label'
      ),
      flex: 1,
      minWidth: 250,
      renderCell: (item) =>
        renderText(item.correspondentBankAccountNumber ?? ''),
    },
    editable && isShowMore
      ? {
          field: 'more' as 'number',
          title: '',
          width: 56,
          flex: 1,
          align: 'center',
          renderCell: (item) =>
            renderMore(t, item, shownOptions, onClickOption),
        }
      : undefined,
  ]);
};

export const useColumns = (props: WithdrawalAccountColumnArgs) => {
  const { t } = useTranslation();

  return useMemo(() => getColumns(t, props), [props, t]);
};
